import firebase from 'utils/firebase';

type RecaptchaMode = 'invisible' | 'normal';

class RecaptchaManager {
  private static instance: RecaptchaManager;
  private verifier: firebase.auth.RecaptchaVerifier | null = null;
  private widgetId: number | null = null;
  private mode: RecaptchaMode = 'invisible';

  private constructor() {}

  static getInstance(): RecaptchaManager {
    if (!RecaptchaManager.instance) {
      RecaptchaManager.instance = new RecaptchaManager();
    }
    return RecaptchaManager.instance;
  }

  async getOrCreateRecaptcha(mode: RecaptchaMode = 'invisible'): Promise<firebase.auth.RecaptchaVerifier> {
    // If we have a verifier and it's the same mode, return it
    if (this.verifier && this.mode === mode) {
      return this.verifier;
    }

    // Clear existing verifier if mode changed or if we need to recreate
    await this.clear();

    try {
      this.verifier = new firebase.auth.RecaptchaVerifier('recaptcha-container', {
        size: mode,
        callback: () => {
          console.log('reCAPTCHA completed successfully');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired, will retry automatically');
          this.resetIfNeeded();
        },
        'error-callback': (error: any) => {
          console.error('reCAPTCHA error:', error);
        }
      });

      this.mode = mode;
      this.widgetId = await this.verifier.render();
      
      console.log(`reCAPTCHA ${mode} widget created with ID:`, this.widgetId);
      return this.verifier;
    } catch (error) {
      console.error('Failed to create reCAPTCHA verifier:', error);
      throw error;
    }
  }

  async resetIfNeeded(): Promise<void> {
    try {
      if (this.widgetId !== null && (window as any).grecaptcha) {
        (window as any).grecaptcha.reset(this.widgetId);
        console.log('reCAPTCHA widget reset');
      }
    } catch (error) {
      console.error('Failed to reset reCAPTCHA:', error);
    }
  }

  async clear(): Promise<void> {
    try {
      if (this.widgetId !== null && (window as any).grecaptcha) {
        (window as any).grecaptcha.reset(this.widgetId);
      }
      if (this.verifier) {
        this.verifier.clear();
      }
    } catch (error) {
      console.error('Failed to clear reCAPTCHA:', error);
    } finally {
      this.verifier = null;
      this.widgetId = null;
    }
  }

  isCreated(): boolean {
    return this.verifier !== null;
  }

  getCurrentMode(): RecaptchaMode {
    return this.mode;
  }
}

// Export singleton instance
export const recaptchaManager = RecaptchaManager.getInstance();

// Helper function for easy usage
export const getRecaptchaVerifier = async (mode: RecaptchaMode = 'invisible'): Promise<firebase.auth.RecaptchaVerifier> => {
  return recaptchaManager.getOrCreateRecaptcha(mode);
};

// Helper function to handle reCAPTCHA errors with fallback
export const signInWithPhoneNumberWithFallback = async (
  phoneNumber: string,
  onSuccess: (result: any) => void,
  onError: (error: any) => void
): Promise<void> => {
  try {
    // Try invisible reCAPTCHA first
    const verifier = await getRecaptchaVerifier('invisible');
    const result = await firebase.auth().signInWithPhoneNumber(phoneNumber, verifier);
    onSuccess(result);
  } catch (error: any) {
    const errorMessage = String(error?.message || '');
    const errorCode = String(error?.code || '');
    
    // Check if it's a reCAPTCHA-related error
    const isRecaptchaError = 
      errorCode === 'auth/network-request-failed' ||
      errorMessage.toLowerCase().includes('recaptcha') ||
      errorMessage.toLowerCase().includes('captcha') ||
      errorMessage.includes('reCAPTCHA service');

    if (isRecaptchaError) {
      console.log('reCAPTCHA invisible failed, trying visible mode...');
      try {
        // Try visible reCAPTCHA as fallback
        const visibleVerifier = await getRecaptchaVerifier('normal');
        const result = await firebase.auth().signInWithPhoneNumber(phoneNumber, visibleVerifier);
        onSuccess(result);
      } catch (fallbackError) {
        console.error('Both invisible and visible reCAPTCHA failed:', fallbackError);
        onError({
          code: 'auth/recaptcha-failed',
          message: 'Could not complete security verification. Please check your internet connection, disable ad-blockers, and try again.'
        });
      }
    } else {
      // Not a reCAPTCHA error, pass through
      onError(error);
    }
  }
};
